'use strict';
import get from 'lodash/get';
import isString from 'lodash/isString';
import toLower from 'lodash/toLower';
import isArray from 'lodash/isArray';
import {Observable} from 'rxjs/Observable';
import {APP_NAME, isCordovaBuild, isWebBuild} from '../../config/constants';


export const GPS_OPTIONS = {
  HIGH: {
    timeout: 5 * 1000,
    maximumAge: 90 * 1000,
    enableHighAccuracy: true
  },
  NORMAL: {
    timeout: 5 * 1000,
    maximumAge: 120 * 1000,
    enableHighAccuracy: true
  },
  LOW: {
    timeout: 5 * 1000,
    maximumAge: 5 * 60 * 1000,
    enableHighAccuracy: false
  },
  CACHED: {
    timeout: 60 * 1000,
    maximumAge: 5 * 60 * 1000,
    enableHighAccuracy: false
  }
};

export const platform = _initPlatform();

export const DEVICE_TYPES = {
  ANDROID: 'android',
  IOS: 'ios',
  CHROME: 'chrome'
};

export function isWebView() {
  return isCordovaBuild();
}

/**
 * @return {Boolean}
 */
export function supportsCamera() {
  return !!(global.window.navigator && global.window.navigator.camera);
}

/**
 * @return {Boolean}
 */
export function supportsVideo() {
  return !!(global.window.navigator && 'navigator.device.capture.captureVideo' && global.window.navigator.camera);
}

export function supportsPickFile() {
  return true;
}

export function supportsDownload() {
  return true;
}

export function supportsImportExport() {
  return isWebBuild();
}

/**
 * @return {Boolean}
 */
export function gpsSupported() {
  return !!(global.window.navigator && global.window.navigator.geolocation);
}

export function getDeviceType() {
  if (platform.isAndroid()) {
    return 'android';
  } else if (platform.isIOS() || platform.isIPad()) {
    return 'ios';
  } else if (!platform.isWebView() && navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
    return 'chrome';
  } else {
    // TODO: add support for other types depending on what this method is needed for
    return null;
  }
}

export function getBuildAwareDeviceType() {
  if (isWebBuild()) {
    return 'browser';
  } else {
    return getDeviceType();
  }
}

export function getUserAgent() {
  return navigator.userAgent;
}

export function getDeviceVersion() {
  if (isWebBuild()) {
    return '';
  } else {
    return window.device && window.device.version;
  }
}

DeviceServicesCordova.$inject = [
  '$log',
  '$window',
  '$settings',
  '$cordovaCamera',
  '$cordovaCapture',
  '$cordovaFile',
  '$cordovaNetwork',
  '$cordovaGeolocation',
  '$cordovaImagePicker',
  'FirebaseUtil',
];

export function DeviceServicesCordova($log, $window, $settings, $cordovaCamera, $cordovaCapture, $cordovaFile, $cordovaNetwork, $cordovaGeolocation, $cordovaImagePicker, FirebaseUtil) {
  const cordovaSupport = !$window.cordova ? {} : initializeCordovaSupport($log, $window, $settings, $cordovaCamera, $cordovaCapture, $cordovaFile, $cordovaNetwork, $cordovaImagePicker, FirebaseUtil);

  return {
    DEVICE_TYPES: DEVICE_TYPES,
    inForeground: () => $window.cordova ? cordovaSupport.getInForeground() : true,
    isOnline: $cordovaNetwork.isOnline,
    isHighSpeed: cordovaSupport.isHighSpeed,
    sanitizeFileName: sanitize,
    sanitizeFilePath: sanitizePath,

    isWebView: platform.isWebView,
    supportsCamera: supportsCamera,
    supportsVideo: supportsVideo,
    supportsPickFile: supportsPickFile,
    supportsDownload: supportsDownload,
    getDeviceType: getDeviceType,
    getBuildAwareDeviceType: getBuildAwareDeviceType,
    getDeviceVersion: getDeviceVersion,
    getUserAgent: getUserAgent,
    capturePhoto: cordovaSupport.capturePhoto,
    captureVideoStream: cordovaSupport.captureVideoStream,
    selectPhotoFromGallery: cordovaSupport.selectPhotoFromGallery,

    GPS_OPTIONS: GPS_OPTIONS,
    /*
     * References:
     *
     * http://www.sno.phy.queensu.ca/~phil/exiftool/TagNames/GPS.html
     * http://www.movable-type.co.uk/scripts/latlong.html
     * http://www.movable-type.co.uk/scripts/latlong-vincenty.html
     */
  };

}

function _initPlatform() {
  try {
    if (global.window.ionic && global.window.ionic.Platform) {
      return global.window.ionic.Platform;
    }
  } catch (e) {
  }

  // ionic is not available yet. lazily retrieve it.
  const _platform = {
    isWebView,
    isIPad: lazyInit('isIPad'),
    isIOS: lazyInit('isIOS'),
    isAndroid: lazyInit('isAndroid'),
  };

  return _platform;

  function lazyInit(fn) {
    return () => {
      if (global.window.ionic && global.window.ionic.Platform) {
        _platform[fn] = global.window.ionic.Platform[fn];
        return global.window.ionic.Platform[fn]();
      } else {
        return false;
      }
    };
  }
}

function initializeCordovaSupport($log, $window, $settings, $cordovaCamera, $cordovaCapture, $cordovaFile, $cordovaNetwork, $cordovaImagePicker, FirebaseUtil) {
  var support = {};
  var isInForeground = true;

  // Device
  support.getInForeground = getInForeground;

  // Camera
  support.getCameraOptions = getCameraOptions;
  support.getVideoOptions = getVideoOptions;
  support.saveFile = saveFile;
  support.ensurePath = ensurePath;
  support.createDir = createDir;
  support.selectPhotoFromGallery = selectPhotoFromGallery;
  support.capturePhoto = capturePhoto;
  support.captureVideoStream = captureVideoStream;

  // Network
  support.isHighSpeed = isHighSpeed;

  // Geolocation

  /*********************************************************************************************************************
   * Device
   ********************************************************************************************************************/

  document.addEventListener('pause', onPause, false);
  document.addEventListener('resume', onResume, false);

  function getInForeground() {
    return isInForeground;
  }

  function onPause() {
    isInForeground = false;
  }

  function onResume() {
    isInForeground = true;
  }

  /*********************************************************************************************************************
   * Camera
   ********************************************************************************************************************/

  function getCameraOptions(source, allowAllTypes) {
    const cameraSettings = $settings.getCameraSettings();
    const cameraOptions = {
      quality: cameraSettings.jpgQuality,
      destinationType: Camera.DestinationType.FILE_URI,
      sourceType: Camera.PictureSourceType.CAMERA,
      allowEdit: false,
      encodingType: Camera.EncodingType.JPEG,
      targetWidth: cameraSettings.cameraResolution,
      targetHeight: cameraSettings.cameraResolution, // 2448 1632 768
      mediaType: Camera.MediaType.PICTURE,
      popoverOptions: new CameraPopoverOptions(0, 32, 320, 480, Camera.PopoverArrowDirection.ARROW_ANY),
      correctOrientation: $settings.correctOrientation, //true,
      saveToPhotoAlbum: false
    };

    if (source === 'gallery') {
      cameraOptions.sourceType = Camera.PictureSourceType.SAVEDPHOTOALBUM;
    }

    if (allowAllTypes) {
      cameraOptions.mediaType = Camera.MediaType.ALLMEDIA;
    }

    return cameraOptions;
  }

  function getVideoOptions() {
    // if (platform.isAndroid()) {
    //   return {
    //     limit: 1,
    //     quality: 0,
    //   };
    // } else {
    //   return {
    //     limit: 1,
    //   };
    // }

    const videoSettings = $settings.getCameraSettings();
    return {
      limit: 1,
      quality: 1,
      ios_quality: 1,
    };
  }

  //Create a random cache ID for the file to save to avoid naming conflicts.
  //iOS camera plugin appears to always return the same file name and breaks view caching
  function saveFile(path, baseName, fileUri) {
    _initStorageRoot();
    var deferred = Promise.defer();
    var cacheName = FirebaseUtil.generateKey();
    var name = _maintainExtension(fileUri, cacheName);
    //var name = fileUri.substring(fileUri.lastIndexOf('/') + 1);
    $log.debug('saveFile with path: ' + path + ', name: ' + name);
    //resolveLocalFileSystemURI
    //resolveLocalFileSystemURL
    $window.resolveLocalFileSystemURL(fileUri, function (file) {
      $log.debug('saveFile resolveLocalFileSystemURI succeeded for fileUri: ' + fileUri);
      support.ensurePath(support.storageRoot, path).then(function (dirEntry) {
        $log.debug('saveFile ensurePath succeeded');
        file.copyTo(dirEntry, name,
          deferred.resolve.bind(deferred),
          deferred.reject.bind(deferred));
      }).catch(function (error) {
        deferred.reject(_convertFileErrorCode(error));
        $log.debug('saveFile resolveLocalFileSystemURI failed', _convertFileErrorCode(error));
      });
    }, function (error) {
      deferred.reject(_convertFileErrorCode(error));
      $log.debug('saveFile resolveLocalFileSystemURI failed', _convertFileErrorCode(error));
    });

    deferred.promise.then(function (result) {
      $log.debug('saveFile moveTo succeeded. return type ' + typeof result);
      return result;
    }, function (result) {
      $log.debug('saveFile moveTo failed. return type ' + typeof result);
      return result;
    });

    return deferred.promise;
  }

  function ensurePath(basePath, path) {
    var deferred = Promise.defer();
    try {
      $window.resolveLocalFileSystemURL(basePath, function (baseDir) {
        $log.debug('ensurePath resolveLocalFileSystemURL succeeded for basePath: ' + basePath);

        if (!path) {
          deferred.resolve(baseDir);
        } else {
          support.createDir(baseDir, path.split('/'),
            deferred.resolve.bind(deferred),
            //deferred.reject.bind(deferred)
            function (error) {
              $log.debug('ensurePath createDir failed', _convertFileErrorCode(error));
              deferred.reject(_convertFileErrorCode(error));
            });
        }
      });
    } catch (error) {
      $log.debug('ensurePath resolveLocalFileSystemURL failed for basePath: ' + basePath, _convertFileErrorCode(error));
      deferred.reject(_convertFileErrorCode(error));
    }
    return deferred.promise;
  }

  function createDir(baseDir, folders, callback, errorHandler) {
    //$log.debug('createDir basePath:' + baseDir + ' folders: ' + folders);
    if (folders[0] == '.' || folders[0] == '') {
      folders = folders.slice(1);
    }

    if (!folders[0]) {
      callback(baseDir);
      return;
    }

    try {
      baseDir.getDirectory(folders[0], {create: true}, function (dirEntry) {
        //$log.debug('createDir successfully created folder: ' + folders[0]);
        if (folders.length > 0) {
          support.createDir(dirEntry, folders.slice(1), callback);
        } else {
          callback(dirEntry);
        }
      }, errorHandler);
    } catch (error) {
      $log.debug('caught exception fetching folder + ' + folders[0], _convertFileErrorCode(error));
      errorHandler(_convertFileErrorCode(error));
    }
  }

  /**
   *
   * @param {string} path the relative file path to save this file locally
   * @param {string} name the file name prefix for this file
   * @param {function} pageCallback this function is passed the file name and current index. should return a string to append to the end of the file name, before the extension
   */
  function capturePhoto(path, name, pageCallback) {
    if (isString(path)) path = sanitizePath(path);
    if (isString(name)) name = sanitize(name);

    if (platform.isAndroid && platform.version() <= 10) {
      return $cordovaCamera
        .getPicture(support.getCameraOptions())
        .then(copyToGallery)
        .then(imageURI => support.saveFile(path, name, imageURI))
        .then(_resolveFile)
        .catch(error => {
          $log.debug('capturePhoto getPicture failed. return type ' + typeof error);
          return error;
        });
    } else {
      return $cordovaCapture
        .captureImage(support.getCameraOptions())
        .then(function (mediaFiles) {
          let imageURI = normalizeMediaResult(mediaFiles);
          if (!imageURI) {
            return Promise.reject('No file captured');
          } else {
            return support.saveFile(path, name, imageURI)
              .then(_resolveFile)
              .catch(error => {
                $log.debug('captureImage failed. return type ' + typeof error);
                return error;
              });
          }
        });
    }

    //$cordovaCamera.cleanup().then(...); // only for FILE_URI

  }

  /**
   *
   * @param {string} path the relative file path to save this file locally
   * @param {string} name the file name prefix for this file
   * @param {function} pageCallback this function is passed the file name and current index. should return a string to append to the end of the file name, before the extension
   */
  function captureVideoStream(path, name, pageCallback) {
    if (isString(path)) path = sanitizePath(path);
    if (isString(name)) name = sanitize(name);

    return $cordovaCapture
      .captureVideo(support.getVideoOptions())
      .then(function (mediaFiles) {
        logMetadata(mediaFiles);
        let imageURI = normalizeMediaResult(mediaFiles);
        if (!imageURI) {
          return Promise.reject('No file captured');
        } else {
          return support.saveFile(path, name, imageURI)
            .then(_resolveFile)
            .catch(error => {
              $log.debug('captureVideo failed. return type ' + typeof error);
              return error;
            });
        }
      });
  }

  function logMetadata(mediaFiles) {
    mediaFiles[0].getFormatData(function (metaData) {
      let avgBitrate = mediaFiles[0].size / metaData.duration;

      console.log('capture bitrate: ', avgBitrate);
      console.log('capture resolution: ', metaData.width, 'x', metaData.height);
      console.log('capture duration: ', metaData.duration);
      //console.log('raw metadata: ', JSON.stringify(metaData));
    });
  }

  function normalizeMediaResult(data) {
    if (isArray(data)) {
      if (data[0]) {
        if (data[0].fullPath.contains('file://')) {
          return data[0].fullPath;
        } else {
          return ('file://' + data[0].fullPath);
        }
      }
    } else {
      console.log('debugging capture. result:', data)
    }
  }

  // handle android content scheme
  function normalizeContentUri(file) {
    const fileURI = file.uri;

    if (fileURI.contains('content://')) {
      var deferred = Promise.defer();
      window.FilePath.resolveNativePath(fileURI, function(result) {
        deferred.resolve(result);
      }, function(error) {
        $log.debug('normalizeContentUri failed.', error);
        deferred.reject(error);
      });

      return deferred.promise;
    } else {
      return fileURI;
    }
  }

  // works on iOS
  function selectPhotoFromGallery(path, name, allowAllTypes) {
    if (isString(path)) path = sanitizePath(path);
    if (isString(name)) name = sanitize(name);

    return $cordovaImagePicker.getPictures(support.getCameraOptions('gallery', allowAllTypes))
      .then(function (imageURIs) {
        const promises = [];

        let normalizedURIs = normalizeGalleryResult(imageURIs);
        if (!normalizedURIs) {
          return Promise.reject('No files selected');
        } else {
          for (const imageURI of normalizedURIs) {
            promises.push(support.saveFile(path, name, imageURI).then(
              _resolveFile,
              function (error) {
                $log.debug('capturePhoto selectPhotoFromGallery failed. return type ' + typeof error);
                return error;
              }
            ));
          }
        }
        return Promise.all(promises);
    });
  }

  function normalizeGalleryResult(data) {
    if (isArray(data)) {
      let imageURIs = [];
      for (const imageURI of data) {
        if (imageURI.path.contains('file://')) {
          imageURIs.push(imageURI.path);
        } else {
          imageURIs.push('file://' + imageURI.path);
        }
      }
      return imageURIs;
    } else {
      console.log('debugging capture. result:', data);
    }
  }

  // developing an alternative solution for Android
  function selectPhotoFromGalleryAndroid(path, name, allowAllTypes) {
    if (isString(path)) path = sanitizePath(path);
    if (isString(name)) name = sanitize(name);

    return $cordovaCamera.getPicture(support.getCameraOptions('gallery', allowAllTypes)).then(function (imageURI) {
      _initStorageRoot();

      var deferred = Promise.defer();

      $window.resolveLocalFileSystemURL(imageURI,
        _resolveFileWithUri(imageURI, deferred),
        function (error) {
          $log.debug('capturePhoto selectPhotoFromGallery failed. return type ' + typeof error);
          deferred.reject(error);
        });

      return deferred.promise;
    });
  }

  function _resolveFile(fileEntry) {
    //file.type = "image/jpeg";
    //return file; //{file: file, uri: file.toURL()};
    var deferred = Promise.defer();

    fileEntry.file(function (file) {
      //$log.debug('_resolveFile succeeded.');
      deferred.resolve({file: file, uri: fileEntry.toURL()});
    }, function (error) {
      $log.debug('capturePhoto failed.', error);
      deferred.reject(error);
    });

    return deferred.promise;
  }

  function copyToGallery(imageURI) {
    if ($settings.copyToGallery && platform.isAndroid) {
      return new Observable(observer => {
        $window.cordova.plugins.imagesaver.saveImageToGallery(
          imageURI,
          function onSaveImageSuccess() {
            observer.next(imageURI);
          },
          function onSaveImageError(error) {
            $log.debug('image copy failed: ', error);
            observer.next(imageURI);
          }, APP_NAME)
      })
      // Do not wait longer than 10 seconds in case of some form of failure
        .timeoutWith(1500, Observable.of(imageURI))
        .mapTo(imageURI)
        .toPromise();
    } else if ($settings.copyToGallery && platform.isIOS) {
      return new Observable(observer => {
        $window.cordova.plugins.saveToCameraRoll.saveImage(
          imageURI,
          'Swift Projects',
          function onSaveImageSuccess() {
            observer.next(imageURI);
          },
          function onSaveImageError(error) {
            $log.debug('image copy failed: ', error);
            observer.next(imageURI);
          }, APP_NAME)
      })
        // Do not wait longer than 10 seconds in case of some form of failure
        .timeoutWith(1500, Observable.of(imageURI))
        .mapTo(imageURI)
        .toPromise();
    } else {
      return Promise.resolve(imageURI);
    }
  }

  // for use with the android execution path
  function _resolveFileWithUri(fileUri, deferred) {
    return function boundResolveFile(fileEntry) {
      fileEntry.file(function (file) {
        deferred.resolve({file: file, uri: fileUri || fileEntry.toURL()});
      }, function (error) {
        $log.debug('pick file failed.', error);
        deferred.reject(error);
      });
    }
  }

  function _maintainExtension(fileUri, newName) {
    var list = fileUri.split('/');
    var originalName = list[list.length - 1];
    var originalExtension = originalName.substr((~-originalName.lastIndexOf(".") >>> 0) + 2) || '';
    var newExtension = newName.substr((~-newName.lastIndexOf(".") >>> 0) + 2) || '';

    if (!newExtension) {
      return newName + '.' + originalExtension;
    } else if (originalExtension.toLowerCase() !== newExtension.toLowerCase()) {
      return newName.substr(0, newName.length - newExtension.length) + originalExtension;
    } else {
      return newName;
    }
  }

  function _convertFileErrorCode(error) {
    var result = {};
    if (error && error.code) {
      result.fileError = $cordovaFileError[error.code];
    }

    if (error && error.message) {
      result.message = error.message;
    }

    return result
  }

  // lazily init StorageRoot
  function _initStorageRoot() {
    if ($window.cordova && $window.cordova.file) {
      support.storageRoot = $window.cordova.file.externalDataDirectory || $window.cordova.file.dataDirectory || $window.cordova.file.syncedDataDirectory;
    } else {
      console.warn('Failed to detect cordova.file in DeviceServices!');
    }
  }


  /*********************************************************************************************************************
   * Network
   ********************************************************************************************************************/

  function isHighSpeed() {
    if (!$cordovaNetwork.isOnline()) {
      return false;
    } else {
      try {
        $log.debug('current connection: ', $cordovaNetwork.getNetwork());
        return !($cordovaNetwork.getNetwork() == $window.Connection.CELL_2G);
      } catch (error) {
        $log.error('received error retrieving network:', error);
        return false;
      }
    }
  }


  /*********************************************************************************************************************
   * Geolocation
   ********************************************************************************************************************/

  // 2 GPS coord helper functions adapted from http://stackoverflow.com/questions/8678371/how-to-convert-gps-degree-to-decimal-and-vice-versa-in-jquery-or-javascript-and
  function _getDMS2DD(days, minutes, seconds, direction) {
    direction = toLower(direction);
    var dd = days + minutes / 60 + seconds / (60 * 60);
    if (direction === 's' || direction === 'w') {
      dd = dd * -1;
    } // Don't do anything for N or E
    return dd;
  }

  //Math.round is used to eliminate the small error caused by rounding in the computer:
  //e.g. 0.2 is not the same as 0.20000000000284
  function _getDD2DMS(dms, type) {
    var direction = dms < 0 ? (type === 'lat' ? 'S' : 'W') : (type === 'lon' ? 'N' : 'E');
    var abs = Math.abs(Math.round(dms * 1000000.));

    //Validate input
    if (type == 'lat' && abs > (90 * 1000000)) {
      throw new Error(' Degrees Latitude must be in the range of -90. to 90. ');
    } else if (type == 'lon' && abs > (180 * 1000000)) {
      throw new Error(' Degrees Longitude must be in the range of -180 to 180. ');
    }


    var days = Math.floor(abs / 1000000);
    var minutes = Math.floor(((abs / 1000000) - days) * 60);
    var seconds = (Math.floor(((((abs / 1000000) - days) * 60) - minutes) * 100000) * 60 / 100000).toFixed();

    return {days: days, minutes: minutes, seconds: seconds, direction: direction, toString: _coordToString}; // TODO: give this an appropriate toString output
  }

  function _coordToString() {
    return this.days + 'º ' + this.minutes + "' " + this.seconds + "'' " + this.direction;
  }

  return support;
}

var $cordovaFileError = {
  1: 'NOT_FOUND_ERR',
  2: 'SECURITY_ERR',
  3: 'ABORT_ERR',
  4: 'NOT_READABLE_ERR',
  5: 'ENCODING_ERR',
  6: 'NO_MODIFICATION_ALLOWED_ERR',
  7: 'INVALID_STATE_ERR',
  8: 'SYNTAX_ERR',
  9: 'INVALID_MODIFICATION_ERR',
  10: 'QUOTA_EXCEEDED_ERR',
  11: 'TYPE_MISMATCH_ERR',
  12: 'PATH_EXISTS_ERR'
};


// simplified version of node-sanitize-filename: https://github.com/parshap/node-sanitize-filename.
// drops custom replacement support
// replaces the truncate method with a rough estimate since Buffer is not available in browsers
var illegalRe = /[\/\?<>\\:\*\|":]/g;
var controlRe = /[\x00-\x1f\x80-\x9f]/g;
var reservedRe = /^\.+$/;
var windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;

// Truncate string by size in bytes
function truncate(str, maxByteSize) {
  if (str.length * 2 <= maxByteSize) {
    return str;
  } else {
    return str.substring(0, Math.floor(maxByteSize / 2))
  }
}

function sanitize(input) {
  var sanitized = input
    .replace(illegalRe, '')
    .replace(controlRe, '')
    .replace(reservedRe, '')
    .replace(windowsReservedRe, '');
  return truncate(sanitized, 255);
}

function sanitizePath(path) {
  if (!path) return path;
  var list = path.split('/');

  for (var i = 0, length = list.length; i < length; i++) {
    if (!list[i]) {
      list.splice(i, 1);
      i--;
      length--;
      continue;
    }

    list[i] = sanitize(list[i]);

    if (!list[i]) {
      list.splice(i, 1);
      i--;
      length--;
    }
  }

  return list.join('/');
}



// WEBPACK FOOTER //
// ./src/js/core-utilities-implementation/device-services/DeviceServicesCordova.js